// File: frontend/src/pages/LeaveRequestsPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { leaveRequestsAPI, classesAPI } from '../services/api';
import Modal from '../components/Modal';

const REASON_LABELS = {
  sick: { text: '病假 (Sick Leave)', color: 'bg-amber-100 text-amber-800' },
  personal: { text: '事假 (Personal Leave)', color: 'bg-blue-100 text-blue-800' },
  exam: { text: '學校考試 (School Exam)', color: 'bg-purple-100 text-purple-800' },
  official: { text: '公假 (Official Leave)', color: 'bg-indigo-100 text-indigo-800' },
  other: { text: '其他 (Other)', color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
};

const STATUS_BADGES = {
  pending: { text: '待審核 (Pending)', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  approved: { text: '已核准 (Approved)', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rejected: { text: '已婉拒 (Rejected)', color: 'bg-red-100 text-red-800 border-red-300' },
};

export default function LeaveRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  // Status Action Modal
  const [selectedReq, setSelectedReq] = useState(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusAction, setStatusAction] = useState('approved'); // 'approved' | 'rejected'
  const [adminNotes, setAdminNotes] = useState('');
  const [grantMakeupCredit, setGrantMakeupCredit] = useState(true);

  // Makeup Scheduling Modal
  const [isMakeupModalOpen, setIsMakeupModalOpen] = useState(false);
  const [makeupDate, setMakeupDate] = useState('');
  const [makeupTime, setMakeupTime] = useState('');
  const [makeupClassId, setMakeupClassId] = useState('');
  const [makeupStatus, setMakeupStatus] = useState('scheduled');
  const [makeupNotes, setMakeupNotes] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await leaveRequestsAPI.getAll(params);
      setRequests(data);
    } catch (err) {
      toast.error('Failed to load leave requests.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchClasses = async () => {
    try {
      const data = await classesAPI.getAll();
      setClasses(data);
    } catch (err) {
      console.error('Failed to fetch classes:', err);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchClasses();
  }, [fetchRequests]);

  const handleOpenStatusModal = (req, action) => {
    setSelectedReq(req);
    setStatusAction(action);
    setAdminNotes(req.admin_notes || '');
    setGrantMakeupCredit(true);
    setIsStatusModalOpen(true);
  };

  const handleConfirmStatus = async (e) => {
    e.preventDefault();
    if (!selectedReq) return;

    try {
      await leaveRequestsAPI.updateStatus(selectedReq.id, {
        status: statusAction,
        admin_notes: adminNotes,
        grant_makeup_credit: grantMakeupCredit,
      });

      toast.success(statusAction === 'approved' ? 'Leave request approved!' : 'Leave request rejected.');
      setIsStatusModalOpen(false);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update request.');
    }
  };

  const handleOpenMakeupModal = (req) => {
    setSelectedReq(req);
    setMakeupDate(req.makeup_date ? req.makeup_date.split('T')[0] : '');
    setMakeupTime(req.makeup_time || '');
    setMakeupClassId(req.makeup_class_id || '');
    setMakeupStatus(req.makeup_status || 'scheduled');
    setMakeupNotes(req.makeup_notes || '');
    setIsMakeupModalOpen(true);
  };

  const handleSaveMakeup = async (e) => {
    e.preventDefault();
    if (!selectedReq) return;

    try {
      await leaveRequestsAPI.scheduleMakeup(selectedReq.id, {
        makeup_date: makeupDate || null,
        makeup_time: makeupTime || null,
        makeup_class_id: makeupClassId || null,
        makeup_status: makeupStatus,
        makeup_notes: makeupNotes,
      });

      toast.success('Make-up class arranged!');
      setIsMakeupModalOpen(false);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to arrange make-up.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this leave request record?')) {
      try {
        await leaveRequestsAPI.delete(id);
        toast.success('Leave request removed.');
        fetchRequests();
      } catch (err) {
        toast.error('Failed to delete request.');
      }
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 pb-4 border-b">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
            Leave & Make-Up Management (請假與補課)
            {pendingCount > 0 && (
              <span className="bg-amber-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full">
                {pendingCount} Pending
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Review student absence requests, auto-sync attendance, and schedule make-up sessions.
          </p>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 dark:text-gray-400 font-medium">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Requests (全部)</option>
            <option value="pending">Pending Review (待審核)</option>
            <option value="approved">Approved (已核准)</option>
            <option value="rejected">Rejected (已婉拒)</option>
          </select>
        </div>
      </div>

      {/* Requests Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">Loading leave requests...</div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          <p className="text-base font-medium">No leave requests found.</p>
          <p className="text-xs mt-1">When students or parents submit a leave request, it will appear here.</p>
        </div>
      ) : (
        <>
          {/* --- MOBILE CARDS VIEW (< 768px) --- */}
          <div className="block md:hidden space-y-3">
            {requests.map((req) => {
              const reasonInfo = REASON_LABELS[req.reason_category] || REASON_LABELS.other;
              const statusInfo = STATUS_BADGES[req.status] || STATUS_BADGES.pending;

              return (
                <div key={`mobile-leave-${req.id}`} className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xs space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <h4 className="font-bold text-base text-gray-900 dark:text-gray-100">{req.student_name}</h4>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {req.class_name || 'Class'} • {new Date(req.request_date).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit', weekday: 'short' })}
                      </div>
                    </div>
                    <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded border ${statusInfo.color}`}>
                      {statusInfo.text}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 items-center text-xs">
                    <span className={`px-2 py-0.5 rounded font-semibold ${reasonInfo.color}`}>
                      {reasonInfo.text}
                    </span>
                    <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded font-bold">
                      Credits: {req.makeup_credits}
                    </span>
                    {req.parent_phone && (
                      <a href={`tel:${req.parent_phone}`} className="text-blue-600 hover:underline">
                        📞 {req.parent_phone}
                      </a>
                    )}
                  </div>

                  {req.reason_text && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-800/60 p-2 rounded border border-gray-100 dark:border-gray-700">
                      "{req.reason_text}"
                    </p>
                  )}

                  {req.makeup_scheduled && (
                    <div className="bg-emerald-50/70 p-2.5 rounded-lg border border-emerald-200 text-xs">
                      <div className="font-bold text-emerald-900">
                        📅 Make-Up: {new Date(req.makeup_date).toLocaleDateString('zh-TW')} {req.makeup_time}
                      </div>
                      <div className="text-emerald-700 text-[11px] mt-0.5">
                        {req.makeup_class_name && `Class: ${req.makeup_class_name} • `}Status: {req.makeup_status}
                      </div>
                    </div>
                  )}

                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-2 justify-end">
                    {req.status === 'pending' && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleOpenStatusModal(req, 'approved')}
                          className="flex-1 py-2 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-xs transition-colors text-center"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenStatusModal(req, 'rejected')}
                          className="flex-1 py-2 px-3 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg shadow-xs transition-colors text-center"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {req.status === 'approved' && (
                      <button
                        type="button"
                        onClick={() => handleOpenMakeupModal(req)}
                        className="flex-1 py-2 px-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg shadow-xs transition-colors text-center"
                      >
                        {req.makeup_scheduled ? 'Edit Make-Up' : '+ Arrange Make-Up'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(req.id)}
                      className="py-2 px-3 bg-gray-100 dark:bg-gray-700 hover:bg-red-50 hover:text-red-700 text-gray-500 dark:text-gray-400 text-xs font-semibold rounded-lg transition-colors"
                      title="Delete record"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* --- DESKTOP TABLE VIEW (>= 768px) --- */}
          <div className="hidden md:block overflow-x-auto border rounded-xl shadow-xs">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm bg-white dark:bg-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-800/60">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-400">Student & Contact</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-400">Class & Leave Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-400">Reason</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-400">Make-Up Info (補課)</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {requests.map((req) => {
                  const reasonInfo = REASON_LABELS[req.reason_category] || REASON_LABELS.other;
                  const statusInfo = STATUS_BADGES[req.status] || STATUS_BADGES.pending;

                  return (
                    <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                      {/* Student */}
                      <td className="px-4 py-3 align-top">
                        <div className="font-bold text-gray-900 dark:text-gray-100">{req.student_name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {req.parent_name && `Parent: ${req.parent_name}`}
                          {req.parent_phone && ` (${req.parent_phone})`}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          {req.parent_line_id || req.line_user_id ? (
                            <span className="text-[10px] bg-green-100 text-green-800 font-bold px-1.5 py-0.2 rounded">
                              💬 LINE Linked
                            </span>
                          ) : (
                            <span className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.2 rounded">
                              No LINE
                            </span>
                          )}
                          <span className="text-[10px] bg-purple-100 text-purple-800 font-bold px-1.5 py-0.2 rounded">
                            Credits: {req.makeup_credits}
                          </span>
                        </div>
                      </td>

                      {/* Class & Date */}
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {new Date(req.request_date).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit', weekday: 'short' })}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                          {req.class_name || 'Individual Class'}
                          {req.class_start_time && ` (${req.class_start_time})`}
                        </div>
                        {req.teacher_name && (
                          <div className="text-[11px] text-gray-400 dark:text-gray-500">Teacher: {req.teacher_name}</div>
                        )}
                      </td>

                      {/* Reason */}
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${reasonInfo.color}`}>
                          {reasonInfo.text}
                        </span>
                        {req.reason_text && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 italic">"{req.reason_text}"</p>
                        )}
                        {req.admin_notes && (
                          <p className="text-[11px] text-blue-600 mt-1">Note: {req.admin_notes}</p>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded border ${statusInfo.color}`}>
                          {statusInfo.text}
                        </span>
                      </td>

                      {/* Make-Up Details */}
                      <td className="px-4 py-3 align-top">
                        {req.makeup_scheduled ? (
                          <div className="text-xs">
                            <div className="font-semibold text-emerald-700">
                              📅 {new Date(req.makeup_date).toLocaleDateString('zh-TW')} {req.makeup_time}
                            </div>
                            {req.makeup_class_name && (
                              <div className="text-gray-500 dark:text-gray-400">Class: {req.makeup_class_name}</div>
                            )}
                            <div className="mt-1">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                                req.makeup_status === 'attended' ? 'bg-emerald-100 text-emerald-800' :
                                req.makeup_status === 'missed' ? 'bg-red-100 text-red-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {req.makeup_status === 'attended' ? '✓ Attended' :
                                 req.makeup_status === 'missed' ? '✕ Missed' : 'Scheduled'}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500 italic">Not scheduled</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right align-top space-x-1 whitespace-nowrap">
                        {req.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleOpenStatusModal(req, 'approved')}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2.5 py-1.5 rounded font-medium shadow-sm cursor-pointer"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenStatusModal(req, 'rejected')}
                              className="bg-red-600 hover:bg-red-700 text-white text-xs px-2.5 py-1.5 rounded font-medium shadow-sm cursor-pointer"
                            >
                              Reject
                            </button>
                          </>
                        )}

                        {req.status === 'approved' && (
                          <button
                            type="button"
                            onClick={() => handleOpenMakeupModal(req)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-2.5 py-1.5 rounded font-medium shadow-sm cursor-pointer"
                          >
                            {req.makeup_scheduled ? 'Edit Make-Up' : '+ Arrange Make-Up'}
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => handleDelete(req.id)}
                          className="text-gray-400 dark:text-gray-500 hover:text-red-600 text-xs px-1.5 py-1 cursor-pointer"
                          title="Delete record"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Approve / Reject Modal */}
      {selectedReq && (
        <Modal
          title={`${statusAction === 'approved' ? 'Approve' : 'Reject'} Leave Request (審核請假)`}
          isOpen={isStatusModalOpen}
          onClose={() => setIsStatusModalOpen(false)}
        >
          <form onSubmit={handleConfirmStatus} className="space-y-4 text-sm">
            <div className="bg-gray-50 dark:bg-gray-800/60 p-3 rounded border text-xs">
              <div>Student: <span className="font-bold text-gray-900 dark:text-gray-100">{selectedReq.student_name}</span></div>
              <div>Leave Date: <span className="font-bold text-gray-900 dark:text-gray-100">{new Date(selectedReq.request_date).toLocaleDateString('zh-TW')}</span></div>
              <div>Reason: <span className="font-semibold text-gray-700 dark:text-gray-300">{selectedReq.reason_category}</span></div>
              {selectedReq.parent_line_id && (
                <div className="text-emerald-700 font-medium mt-1">
                  💬 Parent LINE linked: Will automatically receive push notification upon saving.
                </div>
              )}
            </div>

            {statusAction === 'approved' && (
              <div className="flex items-center space-x-2 bg-emerald-50 p-2.5 rounded border border-emerald-200">
                <input
                  type="checkbox"
                  id="grant_credit"
                  checked={grantMakeupCredit}
                  onChange={(e) => setGrantMakeupCredit(e.target.checked)}
                  className="h-4 w-4 text-emerald-600 rounded"
                />
                <label htmlFor="grant_credit" className="text-xs text-emerald-900 font-medium">
                  Grant +1 Make-Up Class Credit to student (計入1堂補課額度)
                </label>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Teacher Notes / Feedback to Parent (選填):
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="e.g., Get well soon! See you next week."
                className="w-full border rounded p-2 text-xs"
                rows="2"
              ></textarea>
            </div>

            <div className="flex justify-end space-x-2 pt-2 border-t">
              <button
                type="button"
                onClick={() => setIsStatusModalOpen(false)}
                className="bg-gray-300 text-gray-800 dark:text-gray-200 px-4 py-2 rounded text-xs"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`text-white px-4 py-2 rounded text-xs font-semibold ${
                  statusAction === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                Confirm {statusAction === 'approved' ? 'Approval' : 'Rejection'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Schedule Make-Up Modal */}
      {selectedReq && (
        <Modal
          title={`Arrange Make-Up Class (安排補課) - ${selectedReq.student_name}`}
          isOpen={isMakeupModalOpen}
          onClose={() => setIsMakeupModalOpen(false)}
        >
          <form onSubmit={handleSaveMakeup} className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Make-Up Date (補課日期)</label>
                <input
                  type="date"
                  value={makeupDate}
                  onChange={(e) => setMakeupDate(e.target.value)}
                  className="w-full border rounded p-2 text-xs"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Time (時間)</label>
                <input
                  type="text"
                  value={makeupTime}
                  onChange={(e) => setMakeupTime(e.target.value)}
                  placeholder="e.g. 15:30 - 16:30"
                  className="w-full border rounded p-2 text-xs"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Target Class / Slot (補入班級, 選填)</label>
              <select
                value={makeupClassId}
                onChange={(e) => setMakeupClassId(e.target.value)}
                className="w-full border rounded p-2 text-xs bg-white dark:bg-gray-800"
              >
                <option value="">-- Individual 1v1 Make-up / Standalone Slot --</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.day} {c.start_time})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Make-Up Status</label>
              <select
                value={makeupStatus}
                onChange={(e) => setMakeupStatus(e.target.value)}
                className="w-full border rounded p-2 text-xs bg-white dark:bg-gray-800"
              >
                <option value="scheduled">Scheduled (已預約)</option>
                <option value="attended">Attended (已完成補課 - 自動扣減1堂額度)</option>
                <option value="missed">Missed (未出席)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes / Room (備註或教室)</label>
              <textarea
                value={makeupNotes}
                onChange={(e) => setMakeupNotes(e.target.value)}
                placeholder="e.g. Room B with Teacher Sule"
                className="w-full border rounded p-2 text-xs"
                rows="2"
              ></textarea>
            </div>

            <div className="flex justify-end space-x-2 pt-2 border-t">
              <button
                type="button"
                onClick={() => setIsMakeupModalOpen(false)}
                className="bg-gray-300 text-gray-800 dark:text-gray-200 px-4 py-2 rounded text-xs"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-xs font-semibold"
              >
                Save Make-Up Slot
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
